import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, unref as _unref, mergeProps as _mergeProps, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vShow as _vShow, withDirectives as _withDirectives, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "space-y-6" }
const _hoisted_2 = { class: "flex justify-between items-center mb-0 pt-2 pl-2 pr-2" }
const _hoisted_3 = { class: "flex flex-col items-end gap-2" }
const _hoisted_4 = { class: "flex items-center gap-2" }
const _hoisted_5 = { class: "flex flex-col justify-between items-center" }
const _hoisted_6 = { class: "flex gap-2 mb-0.5 justify-between items-center" }
const _hoisted_7 = { class: "min-h-[40px] flex items-center justify-end" }
const _hoisted_8 = { class: "search-container w-full" }
const _hoisted_9 = { class: "search-bar flex justify-between items-center w-full" }
const _hoisted_10 = { class: "flex items-center" }
const _hoisted_11 = { class: "flex items-center gap-4" }
const _hoisted_12 = {
  key: 0,
  class: "flex items-center gap-2"
}
const _hoisted_13 = { class: "summary-stat-box" }
const _hoisted_14 = { class: "summary-stat-value" }
const _hoisted_15 = { class: "summary-stat-box" }
const _hoisted_16 = { class: "summary-stat-box" }
const _hoisted_17 = { class: "summary-stat-box" }
const _hoisted_18 = { class: "d-flex align-center" }
const _hoisted_19 = { class: "flex items-start" }
const _hoisted_20 = { class: "text-subtitle-1 font-weight-bold mb-1" }
const _hoisted_21 = { class: "text-body-1" }
const _hoisted_22 = { class: "mt-1 ml-4" }
const _hoisted_23 = { key: 0 }
const _hoisted_24 = { key: 1 }

import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { VDateInput } from 'vuetify/labs/components';
import { clonePriceList, createPriceList, getPriceLists } from '@/services/priceList.service';
import { dispatchToast } from '@/utils/dispatchToast';
import { formatNumber } from '@/utils';
import { PriceList, PriceListForm, TrustedSeries, PriceRange } from '@/types/priceList';
import { convertLength, emptyPriceList, isPositiveNumber, roundStringToDecimals } from '@/utils/priceListUtils';
import { getCableFromPartNumber, getIsoDate } from '@/utils/copperPriceUtils';
import { CableSpecs, TableHeaderItem } from '@/types/copperPrice.interface';
import PriceListInfo from '@/views/PriceListForm/components/PriceListInfo.vue';
import { getCableSpecs, getCopperPriceHistory } from '@/services/copperPrice.service';
import ErrorDialog from '@/components/ErrorDialog.vue';

type PriceItemType = 'trustedSeries' | 'priceRange';


export default /*@__PURE__*/_defineComponent({
  __name: 'PriceUpdatePreview',
  props: {
  modelValue: {
    type: Object,
    default: () => ({
      dates: null,
      oldCopperPrice: null,
      copperPrice: 0,
      selectedPriceList: null,
    }),
  },
},
  emits: ['update:modelValue'],
  setup(__props, { emit: __emit }) {

const dates = ref(null);
const oldCopperPrice = ref(null);
const copperPrice = ref(0);
const multiplierFactor = ref(20);
const multiplierFactorTouched = ref(false);
const updatedSeries = ref([]);
const hideUpdates = ref(true);
const search = ref('');
const createPriceListDialog = ref(false);
const isNewPriceListValid = ref(false);
const newPriceListForm = ref<PriceListForm>(emptyPriceList);
const priceListOptions = ref<PriceList[]>([]);
const priceListFormRef = ref(null);
const selectedPriceList = ref<PriceList>();
const cableSpecs = ref<CableSpecs[]>([]);
const error = ref({ show: false, title: '', message: '' });

const props = __props;

const emit = __emit;

const isUpdateDisabled = computed(() => {
  const hasPriceList = selectedPriceList.value?.id;
  const hasCopperPrices = oldCopperPrice.value && copperPrice.value;
  const hasItems = selectedPriceList.value?.trustedSeries?.length || selectedPriceList.value?.priceRanges?.length;
  return !hasPriceList || !hasCopperPrices || !hasItems;
});

const isCreatePriceListDisabled = computed(
  () => hideUpdates.value || !updatedSeries.value.length || !updatedSeries.value[0].price,
);

const copperPercentageChange = computed(() => {
  if (!oldCopperPrice.value || !copperPrice.value) return 0;
  return roundStringToDecimals(((copperPrice.value - oldCopperPrice.value) / oldCopperPrice.value) * 100, 2);
});

const isSignificantCopperPriceChange = computed(() => {
  return Math.abs(copperPercentageChange.value) > 5;
});

const updatedSeriesCount = computed(() => updatedSeries.value.filter((item) => item.itemType === 'trustedSeries').length);
const updatedRangesCount = computed(() => updatedSeries.value.filter((item) => item.itemType === 'priceRange').length);

// Summary statistics for price updates
const priceUpdateSummary = computed(() => {
  if (!updatedSeries.value.length) {
    return { count: 0, maxChange: 0, minChange: 0, avgChange: 0 };
  }

  const changes = updatedSeries.value.map((item) => Number(item.changePercent)).filter((val) => !isNaN(val));

  if (!changes.length) {
    return { count: updatedSeries.value.length, maxChange: 0, minChange: 0, avgChange: 0 };
  }

  const count = updatedSeries.value.length;
  const maxChange = Math.max(...changes);
  const minChange = Math.min(...changes);
  const avgChange = roundStringToDecimals(changes.reduce((sum, val) => sum + val, 0) / changes.length, 2);

  return { count, maxChange, minChange, avgChange };
});

const showPriceUpdateSummary = computed(() => {
  return isSignificantCopperPriceChange.value && !hideUpdates.value && updatedSeries.value.length > 0;
});

onMounted(async () => {
  // Initialize values if they are provided
  if (props.modelValue.selectedPriceList) {
    selectedPriceList.value = props.modelValue.selectedPriceList;
  }
  if (props.modelValue.dates) {
    dates.value = props.modelValue.dates;
  }
  if (props.modelValue.oldCopperPrice) {
    oldCopperPrice.value = props.modelValue.oldCopperPrice;
  }
  if (props.modelValue.copperPrice) {
    copperPrice.value = props.modelValue.copperPrice;
  }

  // Fetch price lists and cable specs
  await fetchPriceLists();
  await fetchCableSpecs();

  // Refresh table if a price list is selected
  if (props.modelValue.selectedPriceList) {
    refreshTable();
  }
});

const fetchPriceLists = async () => {
  try {
    const data = await getPriceLists({ attributes: ['id', 'name'] });
    priceListOptions.value = data.filter((priceList) => new Date(priceList.endDate) > new Date());
  } catch (error) {
    dispatchToast('Error fetching Price Lists', { type: 'error' });
  }
};

const fetchCableSpecs = async () => {
  try {
    const response = await getCableSpecs();
    cableSpecs.value = response.data;
  } catch (error) {
    dispatchToast('Error fetching cable specifications:', { type: 'error' });
  }
};

const fetchCopperPriceAverage = async () => {
  try {
    const from = getIsoDate(dates.value[0]);
    const to = getIsoDate(dates.value[dates.value.length - 1]);
    const data = await getCopperPriceHistory(from, to);
    copperPrice.value = data.data.average;
  } catch (error) {
    dispatchToast('Error fetching copper price average', { type: 'error' });
  }
};

const handleManualCopperPriceChange = (value) => {
  dates.value = undefined;
  copperPrice.value = value;
  refreshTable();
};

const selectPriceList = async (value) => {
  selectedPriceList.value = priceListOptions.value.find((priceList) => priceList.id === value);
  oldCopperPrice.value = selectedPriceList.value.copperPrice;
  refreshTable();
};

const tableHeaders = ref<TableHeaderItem[]>([
  { title: 'Type', align: 'start', key: 'type' },
  { title: 'Finished Good P/N', align: 'start', key: 'fullPartNumber', minWidth: '300px' },
  { title: 'Length', align: 'start', key: 'lengthSortValue', minWidth: '125px' },
  { title: 'Current Price', align: 'start', key: 'price' },
  { title: 'Current Adder', align: 'start', key: 'adder' },
  { title: 'Cable', align: 'start', key: 'cable' },
  { title: 'Termination Fee', align: 'start', key: 'termination_fee' },
  { title: 'Copper weight', align: 'start', key: 'copper_weight' },
  { title: 'New Price', align: 'start', key: 'newPrice' },
  { title: 'New Adder', align: 'start', key: 'newAdder' },
  { title: 'Change ($)', align: 'start', key: 'change' },
  { title: 'Change (%)', align: 'start', key: 'changePercent' },
]);

const filteredItems = computed(() => {
  if (!search.value) return updatedSeries.value;
  const searchTerm = search.value.toLowerCase();
  return updatedSeries.value.filter((item) => item.fullPartNumber.toLowerCase().includes(searchTerm));
});

const refreshTable = () => {
  hideUpdates.value = true;
  const missingCables: string[] = [];

  const trustedSeriesItems = prepareItemsData(selectedPriceList.value?.trustedSeries || [], 'trustedSeries', missingCables);

  const priceRangeItems = prepareItemsData(selectedPriceList.value?.priceRanges || [], 'priceRange', missingCables);

  if (missingCables.length) {
    error.value.show = true;
    error.value.title = 'Missing Cable Specifications';
    error.value.message = `Please add Cable Specifications for the following cables:<br><br>`;
    error.value.message += missingCables
      .map((cable) => `<span style="display:inline-block; width: 24px"></span>• ${cable.trim()}`)
      .join('<br>');
    updatedSeries.value = [];
    return;
  }

  updatedSeries.value = [...trustedSeriesItems, ...priceRangeItems];
};

const adjustPrices = () => {
  refreshTable();
  hideUpdates.value = false;
};

/**
 * Unified function to prepare table data for both TrustedSeries and PriceRanges
 * For TrustedSeries: adjusts the price based on copper cost per length
 * For PriceRanges: adjusts the perMeterAdder (basePrice is the termination fee)
 */
const prepareItemsData = (items: TrustedSeries[] | PriceRange[], type: PriceItemType, missingCables: string[]) => {
  const result = [];

  items?.forEach((item) => {
    const cable = getCableFromPartNumber(item.partNumber);
    const cableSpec = cableSpecs.value.find((spec) => spec.cable === cable);

    if (!cableSpec) {
      if (!missingCables.includes(cable)) {
        missingCables.push(cable);
      }
      return;
    }

    // Calculate effective copperPrice: apply multiplier factor to the price change.
    const factor = multiplierFactor.value / 100;
    const effectiveCopperPrice = +copperPrice.value + (+copperPrice.value - oldCopperPrice.value) * factor;

    // Calculate copper cost adjustment per foot
    const newCopperCost = (cableSpec.copper_weight * effectiveCopperPrice) / 1000;
    const oldCopperCost = (cableSpec.copper_weight * oldCopperPrice.value) / 1000;

    if (type === 'trustedSeries') {
      const series = item as TrustedSeries;
      result.push(prepareTrustedSeriesItem(series, cableSpec, cable, oldCopperCost, newCopperCost));
    } else {
      const range = item as PriceRange;
      result.push(preparePriceRangeItem(range, cableSpec, cable, oldCopperCost, newCopperCost));
    }
  });

  return result;
};

const prepareTrustedSeriesItem = (
  series: TrustedSeries,
  cableSpec: CableSpecs,
  cable: string,
  oldCopperCost: number,
  newCopperCost: number,
) => {
  const convertedLength = roundStringToDecimals(
    convertLength(series.lengthInMeters, series.displayLengthUnit, false, false),
    0,
  );
  const lengthInFt = convertLength(series.lengthInMeters, 'F', false, false);
  const oldPerFtAdder = (series.price - cableSpec.termination_fee) / lengthInFt;

  let newPerFtAdder = 0,
    newPrice = 0,
    change = 0,
    changePercent = 0;
  if (oldCopperPrice.value && copperPrice.value) {
    newPerFtAdder = isSignificantCopperPriceChange.value ? oldPerFtAdder - oldCopperCost + newCopperCost : oldPerFtAdder;
    newPrice = isSignificantCopperPriceChange.value ? cableSpec.termination_fee + newPerFtAdder * lengthInFt : series.price;
    change = roundStringToDecimals(newPrice - series.price, 2);
    changePercent = roundStringToDecimals(((newPrice - series.price) / series.price) * 100, 2);
  }

  return {
    ...series,
    type: 'Series',
    itemType: 'trustedSeries' as PriceItemType,
    fullPartNumber: `${series.partNumber}-${series.displayLengthUnit}${convertedLength}`,
    length: `${convertedLength} ${series.displayLengthUnit}`,
    lengthSortValue: series.lengthInMeters,
    price: roundStringToDecimals(series.price, 2),
    adder: roundStringToDecimals(oldPerFtAdder, 2),
    cable,
    termination_fee: cableSpec.termination_fee,
    copper_weight: cableSpec.copper_weight,
    newPrice: roundStringToDecimals(newPrice, 2),
    newAdder: roundStringToDecimals(newPerFtAdder, 2),
    change,
    changePercent,
  };
};

const preparePriceRangeItem = (
  range: PriceRange,
  cableSpec: CableSpecs,
  cable: string,
  oldCopperCost: number,
  newCopperCost: number,
) => {
  // Convert lengths to display unit
  const minLength = roundStringToDecimals(convertLength(range.minLengthInMeters, range.displayLengthUnit, true, false), 0);
  const maxLength = roundStringToDecimals(convertLength(range.maxLengthInMeters, range.displayLengthUnit, true, false), 0);

  // Calculate mid-range length in display unit (with 1 decimal for tooltip)
  const midLengthMeters = (range.minLengthInMeters + range.maxLengthInMeters) / 2;
  const midRangeLength = roundStringToDecimals(convertLength(midLengthMeters, range.displayLengthUnit, true, false), 1);

  // Convert the rounded midRangeLength to feet for price calculations
  const midLengthFt =
    range.displayLengthUnit === 'F' ? Number(midRangeLength) : convertLength(Number(midRangeLength), 'F', false, false);

  const oldPerFtAdder = convertLength(range.perMeterAdder, 'F', false, true);
  const oldPrice = range.basePrice + oldPerFtAdder * midLengthFt;

  let newPerFtAdder = oldPerFtAdder;
  let newPrice = oldPrice;
  let change = 0;
  let changePercent = 0;

  if (oldCopperPrice.value && copperPrice.value) {
    newPerFtAdder = isSignificantCopperPriceChange.value ? oldPerFtAdder - oldCopperCost + newCopperCost : oldPerFtAdder;

    // Calculate new price based on mid-range length: basePrice + newAdder * midLength
    newPrice = range.basePrice + newPerFtAdder * midLengthFt;
    change = roundStringToDecimals(newPrice - oldPrice, 2);
    changePercent = oldPrice > 0 ? roundStringToDecimals(((newPrice - oldPrice) / oldPrice) * 100, 2) : 0;
  }

  // Convert newPerFtAdder back to per-meter for storage (multiply by conversion factor)
  const newPerMeterAdder = convertLength(newPerFtAdder, 'F', false, false);

  return {
    ...range,
    type: 'Range',
    itemType: 'priceRange' as PriceItemType,
    fullPartNumber: `${range.partNumber}-${range.displayLengthUnit}`,
    length: `${minLength}-${maxLength} ${range.displayLengthUnit}`,
    lengthSortValue: (range.minLengthInMeters + range.maxLengthInMeters) / 2,
    midRangeLength,
    price: roundStringToDecimals(oldPrice, 2),
    adder: roundStringToDecimals(oldPerFtAdder, 2),
    cable,
    termination_fee: cableSpec.termination_fee,
    copper_weight: cableSpec.copper_weight,
    newPrice: roundStringToDecimals(newPrice, 2),
    newAdder: roundStringToDecimals(newPerFtAdder, 2),
    newPerMeterAdder: roundStringToDecimals(newPerMeterAdder, 6),
    change,
    changePercent,
  };
};

const createNewPriceList = async () => {
  createPriceListDialog.value = true;
  newPriceListForm.value.name = selectedPriceList.value.name + ' - Updated';
  newPriceListForm.value.copperPrice = isSignificantCopperPriceChange.value ? copperPrice.value : oldCopperPrice.value;
  newPriceListForm.value.partners = selectedPriceList.value.partners;
  newPriceListForm.value.endUsers = selectedPriceList.value.endUsers || [];
  newPriceListForm.value.endUserCaseSensitive = selectedPriceList.value.endUserCaseSensitive || false;
  newPriceListForm.value.endUserExactMatch = selectedPriceList.value.endUserExactMatch || false;
  const today = new Date().toISOString().split('T')[0];
  const sign = copperPercentageChange.value < 0 ? '-' : '';
  newPriceListForm.value.notes = `Created on ${today}, updating copper price from $${oldCopperPrice.value} to $${copperPrice.value}, with a markup of ${sign}${multiplierFactor.value}%`;
  nextTick(() => priceListFormRef.value.validate());
};

const saveNewPriceList = async () => {
  try {
    const newPriceListData = clonePriceList(selectedPriceList.value);
    newPriceListData.name = newPriceListForm.value.name;
    newPriceListData.tag = newPriceListForm.value.tag;
    newPriceListData.startDate = newPriceListForm.value.startDate;
    newPriceListData.endDate = newPriceListForm.value.endDate;
    newPriceListData.copperPrice = newPriceListForm.value.copperPrice;
    newPriceListData.notes = newPriceListForm.value.notes;
    newPriceListData.endUsers = newPriceListForm.value.endUsers;
    newPriceListData.endUserCaseSensitive = newPriceListForm.value.endUserCaseSensitive;
    newPriceListData.endUserExactMatch = newPriceListForm.value.endUserExactMatch;

    // Filter and map trustedSeries items
    const trustedSeriesItems = updatedSeries.value.filter((item) => item.itemType === 'trustedSeries');
    newPriceListData.trustedSeries = trustedSeriesItems.map((series) => ({
      id: undefined,
      partNumber: series.partNumber,
      lengthInMeters: series.lengthInMeters,
      displayLengthUnit: series.displayLengthUnit,
      price: series.newPrice,
    }));

    // Filter and map priceRanges items
    const priceRangeItems = updatedSeries.value.filter((item) => item.itemType === 'priceRange');
    newPriceListData.priceRanges = priceRangeItems.map((range) => ({
      id: undefined,
      partNumber: range.partNumber,
      basePrice: range.basePrice,
      perMeterAdder: range.newPerMeterAdder,
      displayLengthUnit: range.displayLengthUnit,
      minLengthInMeters: range.minLengthInMeters,
      maxLengthInMeters: range.maxLengthInMeters,
    }));

    const response = await createPriceList(
      newPriceListData,
      newPriceListForm.value.partners.map((partner) => partner.id),
    );
    if (!response.success) {
      const message = response?.data?.response?.data?.data?.message || 'Error creating price list';
      dispatchToast(message, { type: 'error' });
      return;
    }

    dispatchToast('Price list created successfully', { type: 'success' });
    closePriceListDialog();
  } catch (error) {
    dispatchToast('Error creating price list', { type: 'error' });
  }
};

const closePriceListDialog = () => {
  createPriceListDialog.value = false;
  newPriceListForm.value.startDate = undefined;
  newPriceListForm.value.endDate = undefined;
  newPriceListForm.value.notes = '';
  newPriceListForm.value.endUsers = [];
  newPriceListForm.value.endUserCaseSensitive = false;
  newPriceListForm.value.endUserExactMatch = false;
  createPriceListDialog.value = false;
};

watch(
  newPriceListForm,
  async (newVal) => {
    if (!priceListFormRef.value) {
      return;
    }
    const { valid } = await priceListFormRef.value.validate();
    isNewPriceListValid.value = valid;
  },
  { deep: true },
);

watch(dates, async (newDates) => {
  if (newDates?.length) {
    await fetchCopperPriceAverage();
    hideUpdates.value = true;
  }
});

// Set multiplier factor defaults when copper direction changes (if not manually touched)
watch(copperPercentageChange, (newValue, oldValue) => {
  if (multiplierFactorTouched.value) return;
  if (newValue > 0 && oldValue <= 0) {
    multiplierFactor.value = 20;
  } else if (newValue < 0 && oldValue >= 0) {
    multiplierFactor.value = 0;
  }
});

const setLastWeekRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 7);
  dates.value = [start, end];
};

const setLastMonthRange = () => {
  const end = new Date();
  const start = new Date();
  start.setMonth(start.getMonth() - 1);
  dates.value = [start, end];
};

const setThreeMonthsRange = () => {
  const end = new Date();
  const start = new Date();
  start.setMonth(start.getMonth() - 3);
  dates.value = [start, end];
};

// Emit updates when local values change
watch(
  [dates, oldCopperPrice, copperPrice, selectedPriceList],
  ([newDates, newOldCopperPrice, newCopperPrice, newSelectedPriceList]) => {
    emit('update:modelValue', {
      dates: newDates,
      oldCopperPrice: isPositiveNumber(newOldCopperPrice) ? Number(newOldCopperPrice) : newOldCopperPrice,
      copperPrice: isPositiveNumber(newCopperPrice) ? Number(newCopperPrice) : newCopperPrice,
      selectedPriceList: newSelectedPriceList,
    });
  },
  { deep: true },
);

return (_ctx: any,_cache: any) => {
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_tooltip = _resolveComponent("v-tooltip")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_data_table = _resolveComponent("v-data-table")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_form = _resolveComponent("v-form")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _cache[27] || (_cache[27] = _createElementVNode("h2", { class: "text-2xl font-semibold" }, "Price Update Preview", -1)),
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            _createVNode(_component_v_select, {
              label: "Select price list",
              modelValue: selectedPriceList.value,
              "onUpdate:modelValue": [
                _cache[0] || (_cache[0] = ($event: any) => ((selectedPriceList).value = $event)),
                selectPriceList
              ],
              "single-line": "",
              "hide-details": "",
              style: {"background-color":"white"},
              width: "300",
              variant: "outlined",
              density: "compact",
              items: priceListOptions.value,
              "item-title": "name",
              "item-value": "id",
              "prepend-inner-icon": "mdi-format-list-bulleted"
            }, null, 8, ["modelValue", "items"]),
            _createVNode(_component_v_text_field, {
              density: "compact",
              label: "Ref. Copper Price",
              modelValue: oldCopperPrice.value,
              "onUpdate:modelValue": [
                _cache[1] || (_cache[1] = ($event: any) => ((oldCopperPrice).value = $event)),
                refreshTable
              ],
              disabled: !!selectedPriceList.value?.copperPrice,
              style: {"min-width":"100px","width":"150px","padding-top":"22px"},
              suffix: "$/Lb",
              "suffix-class": 'text-grey-darken-1'
            }, null, 8, ["modelValue", "disabled"]),
            _createVNode(_component_v_icon, { class: "mx-2" }, {
              default: _withCtx(() => _cache[10] || (_cache[10] = [
                _createTextVNode("mdi-arrow-right")
              ])),
              _: 1
            }),
            _createVNode(_component_v_text_field, {
              density: "compact",
              label: "New Copper Price",
              modelValue: copperPrice.value,
              "onUpdate:modelValue": [
                _cache[2] || (_cache[2] = ($event: any) => ((copperPrice).value = $event)),
                handleManualCopperPriceChange
              ],
              rules: [
              (v) => !v || (!isNaN(v) && v >= 0 && v.toString().trim() !== '') || 'Copper Price must be a positive number',
            ],
              style: {"min-width":"100px","width":"150px","padding-top":"22px"},
              suffix: "$/Lb",
              "suffix-class": 'text-grey-darken-1'
            }, null, 8, ["modelValue", "rules"]),
            _createElementVNode("div", _hoisted_5, [
              _createElementVNode("div", _hoisted_6, [
                _createVNode(_component_v_chip, {
                  size: "x-small",
                  variant: "flat",
                  color: "grey-lighten-1",
                  class: "cursor-pointer text-white",
                  onClick: setLastWeekRange
                }, {
                  default: _withCtx(() => _cache[11] || (_cache[11] = [
                    _createTextVNode(" Last Week ")
                  ])),
                  _: 1
                }),
                _createVNode(_component_v_chip, {
                  size: "x-small",
                  variant: "flat",
                  color: "grey-lighten-1",
                  class: "cursor-pointer text-white",
                  onClick: setLastMonthRange
                }, {
                  default: _withCtx(() => _cache[12] || (_cache[12] = [
                    _createTextVNode(" Last month ")
                  ])),
                  _: 1
                }),
                _createVNode(_component_v_chip, {
                  size: "x-small",
                  variant: "flat",
                  color: "grey-lighten-1",
                  class: "cursor-pointer text-white",
                  onClick: setThreeMonthsRange
                }, {
                  default: _withCtx(() => _cache[13] || (_cache[13] = [
                    _createTextVNode(" Last 3 months ")
                  ])),
                  _: 1
                })
              ]),
              _createVNode(_unref(VDateInput), {
                modelValue: dates.value,
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((dates).value = $event)),
                label: "Copper price average period",
                density: "compact",
                style: {"padding-top":"0"},
                "max-width": "368",
                "min-width": "270",
                multiple: "range",
                "prepend-icon": "",
                "prepend-inner-icon": "mdi-calendar"
              }, null, 8, ["modelValue"])
            ]),
            _createVNode(_component_v_text_field, {
              modelValue: multiplierFactor.value,
              "onUpdate:modelValue": [
                _cache[4] || (_cache[4] = ($event: any) => ((multiplierFactor).value = $event)),
                _cache[5] || (_cache[5] = ($event: any) => (multiplierFactorTouched.value = true))
              ],
              density: "compact",
              label: "Markup Factor",
              style: {"min-width":"80px","width":"140px"},
              prefix: copperPercentageChange.value < 0 ? '-' : '',
              suffix: "%",
              type: "number",
              rules: [(v) => v >= 0 || 'Must be positive'],
              "hide-details": ""
            }, {
              "append-inner": _withCtx(() => [
                _createVNode(_component_v_tooltip, {
                  location: "top",
                  "max-width": "300"
                }, {
                  activator: _withCtx(({ props }) => [
                    _createVNode(_component_v_icon, _mergeProps(props, {
                      size: "16",
                      color: "grey"
                    }), {
                      default: _withCtx(() => _cache[14] || (_cache[14] = [
                        _createTextVNode("mdi-information-outline")
                      ])),
                      _: 2
                    }, 1040)
                  ]),
                  default: _withCtx(() => [
                    _cache[15] || (_cache[15] = _createElementVNode("span", null, "Markup to be added to the percentage change in copper price.", -1))
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }, 8, ["modelValue", "prefix", "rules"]),
            _createVNode(_component_v_btn, {
              color: "primary",
              onClick: adjustPrices,
              disabled: isUpdateDisabled.value
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_icon, null, {
                  default: _withCtx(() => _cache[16] || (_cache[16] = [
                    _createTextVNode("mdi-refresh")
                  ])),
                  _: 1
                }),
                _cache[17] || (_cache[17] = _createTextVNode()),
                _cache[18] || (_cache[18] = _createElementVNode("span", { class: "ml-2" }, "Adjust prices", -1))
              ]),
              _: 1
            }, 8, ["disabled"])
          ]),
          _createElementVNode("div", _hoisted_7, [
            _withDirectives(_createElementVNode("div", {
              class: _normalizeClass({
              'bg-blue-100': isSignificantCopperPriceChange.value,
              'bg-amber-100': !isSignificantCopperPriceChange.value,
              'rounded-lg px-4 py-2 flex items-center': true,
            })
            }, [
              _createVNode(_component_v_icon, {
                color: isSignificantCopperPriceChange.value ? 'blue-darken-2' : 'amber-darken-2',
                class: "mr-2"
              }, {
                default: _withCtx(() => _cache[19] || (_cache[19] = [
                  _createTextVNode("mdi-information")
                ])),
                _: 1
              }, 8, ["color"]),
              _createElementVNode("span", null, [
                _createTextVNode("Copper price has " + _toDisplayString(copperPercentageChange.value >= 0 ? 'increased' : 'decreased') + " by ", 1),
                _createElementVNode("span", {
                  class: _normalizeClass(copperPercentageChange.value >= 0 ? 'text-green-600' : 'text-red-600')
                }, _toDisplayString(copperPercentageChange.value >= 0 ? '+' + copperPercentageChange.value : copperPercentageChange.value) + "% ", 3),
                _cache[26] || (_cache[26] = _createTextVNode(" . ")),
                (isSignificantCopperPriceChange.value)
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                      _cache[20] || (_cache[20] = _createTextVNode(" Prices ")),
                      _cache[21] || (_cache[21] = _createElementVNode("strong", null, " will be updated", -1)),
                      _cache[22] || (_cache[22] = _createTextVNode(" accordingly as the change is more than 5%. "))
                    ], 64))
                  : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                      _cache[23] || (_cache[23] = _createTextVNode(" Prices ")),
                      _cache[24] || (_cache[24] = _createElementVNode("strong", null, " will not be updated", -1)),
                      _cache[25] || (_cache[25] = _createTextVNode(" as the change is less than 5%. "))
                    ], 64))
              ])
            ], 2), [
              [_vShow, oldCopperPrice.value && copperPrice.value]
            ])
          ])
        ])
      ]),
      _createElementVNode("div", _hoisted_8, [
        _createElementVNode("div", _hoisted_9, [
          _createElementVNode("div", _hoisted_10, [
            _createVNode(_component_v_text_field, {
              modelValue: search.value,
              "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((search).value = $event)),
              label: "Search by P/N",
              "single-line": "",
              "hide-details": "",
              class: "search-bar-input",
              style: {"background-color":"white"},
              width: "300",
              variant: "outlined",
              density: "compact",
              "prepend-inner-icon": "mdi-magnify"
            }, null, 8, ["modelValue"])
          ]),
          _createElementVNode("div", _hoisted_11, [
            (showPriceUpdateSummary.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                  _createElementVNode("div", _hoisted_13, [
                    _cache[28] || (_cache[28] = _createElementVNode("span", { class: "summary-stat-label" }, "Total Items", -1)),
                    _createElementVNode("span", _hoisted_14, _toDisplayString(priceUpdateSummary.value.count), 1)
                  ]),
                  _createElementVNode("div", _hoisted_15, [
                    _cache[29] || (_cache[29] = _createElementVNode("span", { class: "summary-stat-label" }, "Max Change", -1)),
                    _createElementVNode("span", {
                      class: _normalizeClass(["summary-stat-value", priceUpdateSummary.value.maxChange >= 0 ? 'text-green-600' : 'text-red-600'])
                    }, _toDisplayString(priceUpdateSummary.value.maxChange >= 0 ? '+' : '') + _toDisplayString(priceUpdateSummary.value.maxChange) + "% ", 3)
                  ]),
                  _createElementVNode("div", _hoisted_16, [
                    _cache[30] || (_cache[30] = _createElementVNode("span", { class: "summary-stat-label" }, "Min Change", -1)),
                    _createElementVNode("span", {
                      class: _normalizeClass(["summary-stat-value", priceUpdateSummary.value.minChange >= 0 ? 'text-green-600' : 'text-red-600'])
                    }, _toDisplayString(priceUpdateSummary.value.minChange >= 0 ? '+' : '') + _toDisplayString(priceUpdateSummary.value.minChange) + "% ", 3)
                  ]),
                  _createElementVNode("div", _hoisted_17, [
                    _cache[31] || (_cache[31] = _createElementVNode("span", { class: "summary-stat-label" }, "Avg Change", -1)),
                    _createElementVNode("span", {
                      class: _normalizeClass(["summary-stat-value", priceUpdateSummary.value.avgChange >= 0 ? 'text-green-600' : 'text-red-600'])
                    }, _toDisplayString(priceUpdateSummary.value.avgChange >= 0 ? '+' : '') + _toDisplayString(priceUpdateSummary.value.avgChange) + "% ", 3)
                  ])
                ]))
              : _createCommentVNode("", true),
            _createVNode(_component_v_btn, {
              color: "primary",
              onClick: createNewPriceList,
              disabled: isCreatePriceListDisabled.value
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_icon, null, {
                  default: _withCtx(() => _cache[32] || (_cache[32] = [
                    _createTextVNode("mdi-plus")
                  ])),
                  _: 1
                }),
                _cache[33] || (_cache[33] = _createTextVNode()),
                _cache[34] || (_cache[34] = _createElementVNode("span", { class: "ml-2" }, "Create New Pricelist", -1))
              ]),
              _: 1
            }, 8, ["disabled"])
          ])
        ])
      ]),
      _createVNode(_component_v_data_table, {
        headers: tableHeaders.value,
        items: filteredItems.value,
        search: search.value,
        "items-per-page": 20,
        class: "elevation-1",
        density: "compact"
      }, {
        "item.type": _withCtx(({ item }) => [
          _createElementVNode("span", {
            class: _normalizeClass(["px-3 py-1 rounded-full text-xs font-medium", item.itemType === 'trustedSeries' ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-500'])
          }, _toDisplayString(item.type), 3)
        ]),
        "item.lengthSortValue": _withCtx(({ item }) => [
          _createElementVNode("span", _hoisted_18, [
            _createTextVNode(_toDisplayString(item.length) + " ", 1),
            (item.itemType === 'priceRange')
              ? (_openBlock(), _createBlock(_component_v_tooltip, {
                  key: 0,
                  location: "top",
                  "max-width": "350"
                }, {
                  activator: _withCtx(({ props }) => [
                    _createVNode(_component_v_icon, _mergeProps(props, {
                      size: "14",
                      color: "grey",
                      class: "ml-1"
                    }), {
                      default: _withCtx(() => _cache[35] || (_cache[35] = [
                        _createTextVNode("mdi-information-outline")
                      ])),
                      _: 2
                    }, 1040)
                  ]),
                  default: _withCtx(() => [
                    _createElementVNode("span", null, [
                      _cache[36] || (_cache[36] = _createTextVNode("Price calculations for ranges are based on the mid-range length for reference: ")),
                      _createElementVNode("strong", null, _toDisplayString(item.midRangeLength) + " " + _toDisplayString(item.displayLengthUnit), 1)
                    ])
                  ]),
                  _: 2
                }, 1024))
              : _createCommentVNode("", true)
          ])
        ]),
        "item.price": _withCtx(({ item }) => [
          _createTextVNode(_toDisplayString(_unref(formatNumber)(item.price)) + " $ ", 1)
        ]),
        "item.adder": _withCtx(({ item }) => [
          _createTextVNode(_toDisplayString(_unref(formatNumber)(item.adder)), 1)
        ]),
        "item.newPrice": _withCtx(({ item }) => [
          _createElementVNode("span", {
            class: _normalizeClass({ 'text-blue-500': !hideUpdates.value, 'text-transparent': hideUpdates.value })
          }, _toDisplayString(_unref(formatNumber)(item.newPrice)) + " $", 3)
        ]),
        "item.newAdder": _withCtx(({ item }) => [
          _createElementVNode("span", {
            class: _normalizeClass({ 'text-blue-500': !hideUpdates.value, 'text-transparent': hideUpdates.value })
          }, _toDisplayString(_unref(formatNumber)(item.newAdder)), 3)
        ]),
        "item.change": _withCtx(({ item }) => [
          _createElementVNode("span", {
            class: _normalizeClass({ 'text-green-500': item.change > 0, 'text-red-500': item.change < 0, 'text-transparent': hideUpdates.value })
          }, _toDisplayString(_unref(formatNumber)(item.change, 2, true, true)) + " $ ", 3)
        ]),
        "item.changePercent": _withCtx(({ item }) => [
          _createElementVNode("span", {
            class: _normalizeClass({
            'text-green-500': item.changePercent > 0,
            'text-red-500': item.changePercent < 0,
            'text-transparent': hideUpdates.value,
          })
          }, _toDisplayString(_unref(formatNumber)(item.changePercent, 2, true, true)) + " % ", 3)
        ]),
        "no-data": _withCtx(() => _cache[37] || (_cache[37] = [
          _createElementVNode("div", { class: "text-center py-8 text-gray-500" }, " No matching cables found. Please select a price list with hybrid cables. ", -1)
        ])),
        _: 1
      }, 8, ["headers", "items", "search"])
    ]),
    _createVNode(_component_v_dialog, {
      modelValue: createPriceListDialog.value,
      "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((createPriceListDialog).value = $event)),
      "max-width": "450"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_card, null, {
          default: _withCtx(() => [
            _createVNode(_component_v_card_title, { class: "text-h5 pl-12 pt-4" }, {
              default: _withCtx(() => _cache[38] || (_cache[38] = [
                _createTextVNode("Create New Price List")
              ])),
              _: 1
            }),
            _createVNode(_component_v_card_text, { class: "px-10" }, {
              default: _withCtx(() => [
                _createVNode(_component_v_form, {
                  ref_key: "priceListFormRef",
                  ref: priceListFormRef
                }, {
                  default: _withCtx(() => [
                    _createVNode(PriceListInfo, {
                      form: newPriceListForm.value,
                      "onUpdate:form": _cache[7] || (_cache[7] = ($event: any) => ((newPriceListForm).value = $event)),
                      "is-empty-pricelist": true,
                      "is-limited-version": true,
                      onSave: saveNewPriceList,
                      onCancel: closePriceListDialog,
                      "is-save-disabled": !isNewPriceListValid.value
                    }, null, 8, ["form", "is-save-disabled"])
                  ]),
                  _: 1
                }, 512),
                _createElementVNode("div", {
                  class: _normalizeClass({
            'bg-amber-50': !isSignificantCopperPriceChange.value,
            'bg-blue-50': isSignificantCopperPriceChange.value,
            'mt-6 rounded-lg p-4': true,
          })
                }, [
                  _createElementVNode("div", _hoisted_19, [
                    _createElementVNode("div", null, [
                      _createElementVNode("div", _hoisted_20, [
                        _createVNode(_component_v_icon, {
                          color: isSignificantCopperPriceChange.value ? 'blue-darken-2' : 'amber-darken-2',
                          class: "mr-2"
                        }, {
                          default: _withCtx(() => _cache[39] || (_cache[39] = [
                            _createTextVNode("mdi-information")
                          ])),
                          _: 1
                        }, 8, ["color"]),
                        _cache[40] || (_cache[40] = _createTextVNode(" New Price List Summary "))
                      ]),
                      _createElementVNode("div", _hoisted_21, [
                        _createElementVNode("span", null, [
                          _createTextVNode("Copper price has " + _toDisplayString(copperPercentageChange.value >= 0 ? 'increased' : 'decreased') + " by ", 1),
                          _createElementVNode("span", {
                            class: _normalizeClass(copperPercentageChange.value >= 0 ? 'text-green-600' : 'text-red-600')
                          }, _toDisplayString(copperPercentageChange.value >= 0 ? '+' + copperPercentageChange.value : copperPercentageChange.value) + "% ", 3),
                          (isSignificantCopperPriceChange.value)
                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                _cache[43] || (_cache[43] = _createTextVNode(" . Prices will be updated accordingly as the change is more than 5%: ")),
                                _createElementVNode("ul", _hoisted_22, [
                                  (updatedSeriesCount.value)
                                    ? (_openBlock(), _createElementBlock("li", _hoisted_23, [
                                        _createElementVNode("strong", null, _toDisplayString(updatedSeriesCount.value), 1),
                                        _cache[41] || (_cache[41] = _createTextVNode(" trusted series "))
                                      ]))
                                    : _createCommentVNode("", true),
                                  (updatedRangesCount.value)
                                    ? (_openBlock(), _createElementBlock("li", _hoisted_24, [
                                        _createElementVNode("strong", null, _toDisplayString(updatedRangesCount.value), 1),
                                        _cache[42] || (_cache[42] = _createTextVNode(" price ranges "))
                                      ]))
                                    : _createCommentVNode("", true)
                                ])
                              ], 64))
                            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                _cache[44] || (_cache[44] = _createTextVNode(" . Prices ")),
                                _cache[45] || (_cache[45] = _createElementVNode("strong", null, " will not be updated ", -1)),
                                _cache[46] || (_cache[46] = _createTextVNode(" for any products as the change is less than 5%. "))
                              ], 64))
                        ])
                      ])
                    ])
                  ])
                ], 2)
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(ErrorDialog, {
      modelValue: error.value.show,
      "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((error.value.show) = $event)),
      title: error.value.title,
      message: error.value.message
    }, null, 8, ["modelValue", "title", "message"])
  ], 64))
}
}

})